import {
  ANALYTICS_ERROR,
  BUY_IT_AGAIN_CHECKOUT_CLICKED,
  BUY_IT_AGAIN_NO_REORDERABLE_ITEMS,
  BUY_IT_AGAIN_PAGE_NUMBER_CHANGED,
  BUY_IT_AGAIN_PRESENTED,
  BUY_IT_AGAIN_QUANTITY_CHANGED,
  BUY_NOW_ADD_TO_CART_FAILED,
  BUY_NOW_ATTEMPTED,
  BUY_NOW_FAILED,
  BUY_NOW_SUCCESS,
  DOC_CONVERSION_MODAL_PRESENTED_TO_CUSTOMER,
  DOCUMENT_EDIT_CLICKED,
  DOCUMENT_URL_FETCH_FAILED,
  EXPRESS_CHECKOUT_ADDED_TO_CART_EVENT_NAME,
  FIND_ORDER_ATTEMPTED,
  FIND_ORDER_FAILED,
  FIND_ORDER_SUCCESS,
  LEGACY_DOCUMENT_CONVERSION_ATTEMPTED,
  LEGACY_DOCUMENT_CONVERSION_FAILED,
  LEGACY_DOCUMENT_CONVERSION_SUCCEEDED,
  LEGACY_DOCUMENT_REMEDIATION_MODAL_PRESENTED_TO_CUSTOMER,
  LEGACY_EDIT_DOCUMENT_URL_CONVERSION_SUCCESS_EVENT_NAME,
  MY_ACCOUNT_PAGE_SECTION,
  MY_ACCOUNT_PAGE_STAGE,
  ORDER_HISTORY_TAB_CHANGED,
  PRODUCT_REORDERED_LABEL,
  PROFILE_FETCH_ERROR,
  REORDER_ATTEMPTED,
  REORDER_FAILED,
  REORDER_SUCCESS,
  REORDER_UNAVAILABLE_FOR_PRODUCT,
  EXPRESS_CHECKOUT_ADD_TO_CART_CLICK,
} from '../../constants/newRelic';
import type { TrackOrderHistoryExperienceTabChangedProps } from '../../types/newRelic.types';
import type { LineItemData } from '../../types/OrderHistory.types';
import { createVariantFromProductAttrs } from '../../utils/productVariantCreator';
import { trackOrderAction } from '../trackingApi';
import {
  createTrackingErrorData,
  createTrackingEventData,
  trackErrorWrapper,
  trackEventWrapper,
} from './newRelicTrackingUtils';

export const trackAnalyticsError = (error: Error, additionalData: object) => {
  const formattedErrorData = createTrackingErrorData(
    ANALYTICS_ERROR,
    error,
    additionalData
  );
  trackErrorWrapper(error, formattedErrorData);
};

/**
 * A customer has clicked to switch tabs (between "Buy It Again" and traditional Order History)
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackOrderHistoryExperienceTabChanged = (
  additionalData: TrackOrderHistoryExperienceTabChangedProps
) => {
  const eventProperties = createTrackingEventData(
    ORDER_HISTORY_TAB_CHANGED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/********************* FIND ORDER  ****************************/

/**
 * A customer attempted to find an order by orderNumber on the Order History page
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackFindOrderAttempted = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    FIND_ORDER_ATTEMPTED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * A customer failed to find an order by orderNumber on the Order History page
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackErrorWrapper
 */
export const trackFindOrderAttemptFailed = (
  error: Error,
  additionalData: object
) => {
  const errorData = createTrackingErrorData(
    FIND_ORDER_FAILED,
    error,
    additionalData
  );
  trackErrorWrapper(error, errorData);
};

/**
 * A customer successfully found an order by orderNumber on the Order History page
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackFindOrderSuccess = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    FIND_ORDER_SUCCESS,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * Fire an event when there is an error fetching profile data
 * @param {Error} error - the error object
 * @returns {void} - calls trackErrorWrapper
 */
export const trackFetchProfileError = (error: Error) => {
  const errorData = createTrackingErrorData(PROFILE_FETCH_ERROR, error, {});
  trackErrorWrapper(error, errorData);
};

/*******************  BIA ******************************/

/**
 * The page on "Buy It Again" changed
 * @param {number} totalPageCount
 * @param {number} currentPageIndex
 * @param {number} selectedPageIndex
 * @returns {void} - calls trackEventWrapper
 */
export const trackBuyItAgainPageChanged = ({
  totalPageCount,
  currentPageIndex,
  selectedPageIndex,
}: {
  totalPageCount: number;
  currentPageIndex: number;
  selectedPageIndex: number;
}) => {
  const trackingEventAdditionalData = {
    totalPageCount,
    currentPageIndex,
    selectedPageIndex,
  };
  const eventProperties = createTrackingEventData(
    BUY_IT_AGAIN_PAGE_NUMBER_CHANGED,
    trackingEventAdditionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * The "Buy It Again" experience was presented to a customer
 * @param {object} additionalData Additional data to include in the tracking event.
 * @param {number} totalPageCount
 * @returns {void} - calls trackEventWrapper
 */
export const trackBuyItAgainPresentedToCustomer = (
  additionalData: object,
  totalPageCount: number
) => {
  const eventProperties = createTrackingEventData(
    BUY_IT_AGAIN_PRESENTED,
    additionalData
  );
  const enhancedEventProperties = { ...eventProperties, totalPageCount };
  trackEventWrapper(enhancedEventProperties);
};

/**
 * A customer has no reorderable items to display for "Buy It Again" experience
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackShopperHasNoBuyItAgainItems = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    BUY_IT_AGAIN_NO_REORDERABLE_ITEMS,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * A customer has clicked to checkout via the "Buy It Again" experience
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackBuyItAgainCheckoutClicked = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    BUY_IT_AGAIN_CHECKOUT_CLICKED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/************* DOCUMENT CONVERSION / REMEDIATION ***********************/

/**
 * A customer attempted to convert a legacy document
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackLegacyDocumentConversionAttempted = (
  additionalData: object
) => {
  const eventProperties = createTrackingEventData(
    LEGACY_DOCUMENT_CONVERSION_ATTEMPTED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * A customer successfully converted a legacy document
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackLegacyDocumentConversionSuccess = (
  additionalData: object
) => {
  const eventProperties = createTrackingEventData(
    LEGACY_DOCUMENT_CONVERSION_SUCCEEDED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * A customer failed to convert a legacy document
 * @param {object} error - the error object
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackErrorWrapper
 */
export const trackLegacyDocumentConversionAttemptFailed = (
  error: Error,
  additionalData: object
) => {
  const errorData = createTrackingErrorData(
    LEGACY_DOCUMENT_CONVERSION_FAILED,
    error,
    additionalData
  );
  trackErrorWrapper(error, errorData);
};

/**
 * Fire an event that tracks when a user clicks the edit button for a migrated order
 * occurs after the doc conversion call comes back as a successful operation (i.e. editDocumentUrl is present on the response).
 *
 * @param {object} additionalData
 */
export function trackLegacyEditDocumentUrlConversionSuccess(
  lineItem: LineItemData,
  sourcePage: string
) {
  const additionalData = {
    label: 'Legacy Edit URL returned from DocGateway',
    pageSection: 'My Account',
    pageStage: 'My Account',
    pageName: `My Account: ${sourcePage}`,
    product_id: lineItem.id,
    name: lineItem.productName,
    core_product_id: lineItem.productKey,
    price: lineItem.pricing.totalDiscountedPrice?.centAmount,
    list_price: lineItem.pricing.totalListPrice?.centAmount,
    quantity: 1,
    sales_quantity: lineItem.quantity,
    discount:
      typeof lineItem?.pricing.totalListPrice?.centAmount === 'number' &&
      typeof lineItem?.pricing.totalDiscountedPrice?.centAmount === 'number'
        ? lineItem.pricing.totalListPrice.centAmount -
          lineItem.pricing.totalDiscountedPrice.centAmount
        : null,
    variant: createVariantFromProductAttrs(lineItem.attributes), // was lineItem.displayProductAttributes
  };

  trackOrderAction({
    actionName: LEGACY_EDIT_DOCUMENT_URL_CONVERSION_SUCCESS_EVENT_NAME,
    additionalData,
  });
}

/**
 * Fire an event that states a customer was presented with an option to fix their
 * failed legacy doc
 *
 * @param {object} additionalData
 */
export function docRemediationModalPresentedToCustomer(additionalData: object) {
  trackOrderAction({
    actionName: LEGACY_DOCUMENT_REMEDIATION_MODAL_PRESENTED_TO_CUSTOMER,
    additionalData,
  });
}

/**
 * A customers reorder or edit attempt of a migrated item failed, due to doc conversion failure.
 * They were presented with an option to remediate their document
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackDocumentConversionRemediationPresentedToCustomer = (
  additionalData: object
) => {
  const eventProperties = createTrackingEventData(
    DOC_CONVERSION_MODAL_PRESENTED_TO_CUSTOMER,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * Fire an event when there is an error fetching document url
 * @param {Error} error - the error object
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackErrorWrapper
 */
export const trackFetchDocumentUrlError = (
  error: Error,
  additionalData: object
) => {
  const errorData = createTrackingErrorData(
    DOCUMENT_URL_FETCH_FAILED,
    error,
    additionalData
  );
  trackErrorWrapper(error, errorData);
};

/**
 * A customer has clicked to edit their document
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackDocumentEditClicked = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    DOCUMENT_EDIT_CLICKED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/****************** REORDER ***************************/

/**
 * A customer attempted to reorder a product which is unavailable
 * Inspect `additionalData.failureReason` to determine failure type (`REORDER_NOT_PERMITTED`, `RETIRED_PRODUCT`, etc.)
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackErrorWrapper
 */
export const trackReorderUnavailableForProduct = (additionalData: object) => {
  const error = new Error('Reorder unavailable for product');
  const errorData = createTrackingErrorData(
    REORDER_UNAVAILABLE_FOR_PRODUCT,
    error,
    additionalData
  );
  trackErrorWrapper(error, errorData);
};

/**
 * A customer attempted to reorder a set of item(s)
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackReorderAttempted = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    REORDER_ATTEMPTED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * A customer successfully reordered a set of item(s)
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackReorderSuccess = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    REORDER_SUCCESS,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * A customer failed to reorder a set of item(s)
 * @param {object} error - the error object
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackErrorWrapper
 */
export const trackReorderAttemptFailed = (
  error: Error,
  additionalData: object
) => {
  const errorData = createTrackingErrorData(
    REORDER_FAILED,
    error,
    additionalData
  );
  trackErrorWrapper(error, errorData);
};

/**
 * A customer has changed the quantity to reorder via the "Buy It Again" Experience
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackBuyItAgainQuantityChanged = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    BUY_IT_AGAIN_QUANTITY_CHANGED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * Fire an event that tracks when a user clicks the buy now button
 * occurs after the item has been added to cart successfully.
 *
 * @param {string} lineItemId - line item id of item that is to be bought
 * @param {string} displayName - display name of the item that is to be bought
 * @param {string} productKey - product key of item that is to be bought
 * @param {string} page - name of the page the action is occurring on
 */
export function trackExpressCheckoutProductAddedToCart({
  lineItemId,
  displayName,
  productKey,
  pageName,
}: {
  lineItemId: string;
  displayName: string;
  productKey: string;
  pageName: string;
}) {
  const additionalData = {
    label: PRODUCT_REORDERED_LABEL,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName,
    product_id: lineItemId,
    name: displayName,
    core_product_id: productKey,
  };

  trackOrderAction({
    actionName: EXPRESS_CHECKOUT_ADDED_TO_CART_EVENT_NAME,
    additionalData,
  });
}

/**
 * A customer attempted to buy an item now
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 * */
export const trackBuyNowAttempted = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    BUY_NOW_ATTEMPTED,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * A customer successfully bought now an item
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackBuyNowSuccess = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    BUY_NOW_SUCCESS,
    additionalData
  );
  trackEventWrapper(eventProperties);
};

/**
 * A customer failed to buy now, due to a failed call to OREOs
 * @param {object} error
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackErrorWrapper
 */
export const trackBuyNowAttemptFailed = (
  error: Error,
  additionalData: object
) => {
  const errorData = createTrackingErrorData(
    BUY_NOW_FAILED,
    error,
    additionalData
  );
  trackErrorWrapper(error, errorData);
};

/**
 * A customer failed to add buy now item to cart
 * @param {object} error
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackErrorWrapper
 */
export const trackBuyNowAddToCartFailed = (
  error: Error,
  additionalData: object
) => {
  const errorData = createTrackingErrorData(
    BUY_NOW_ADD_TO_CART_FAILED,
    error,
    additionalData
  );
  trackErrorWrapper(error, errorData);
};

/**
 * Successfully added an item to the alt cart for express checkout
 * @param {object} additionalData Additional data to include in the tracking event.
 * @returns {void} - calls trackEventWrapper
 */
export const trackExpressCheckoutAddToCartClick = (additionalData: object) => {
  const eventProperties = createTrackingEventData(
    EXPRESS_CHECKOUT_ADD_TO_CART_CLICK,
    additionalData
  );
  trackEventWrapper(eventProperties);
};
